import {
  ContractTypeData,
  ContractTypeRequest,
  useLinkDealerToContractType,
  useContractType,
  useUpdateContractType,
  useCreateContractType,
  useContractTypeNames,
  useTemplatesByCompany,
  useTemplatesByContractType,
  useLinkTemplateToContractType,
  useUnlinkTemplateFromContractType,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  contractTypeOptions,
  FormFieldItem,
  generateReadableErrorMessages,
  getUserData,
  useViewNavigate,
  validateFields,
} from 'common'
import {DropdownItem} from 'components/ui-kit'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useLocation, useParams} from 'react-router-dom'

/**
 * Initial data
 */
const initialContractTypeData: ContractTypeData = {
  name: '',
  type: '',
  note: '',
  companyId: null,
  active: true,
  disclaimer: '',
  lockFields: false,
  displayExtraCosts: false,
  displayInstallationCosts: false,
  displayServicePrice: false,
  advancedPayment: 1,
  accessoryDiscount: 0,
  productDiscount: 0,
  serviceDiscount: 0,
  functionInterest: 0,
  interest: 0,
  accessoryInsuranceMinimum: 0,
  productInsuranceMinimum: 0,
  insurancePercentage: 0,
  allowedDealers: [],
  expiry: undefined,
  lengths: [18, 24, 36, 60],
}

export function useContractTypeFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [contractTypeData, setContractTypeData] = useState<ContractTypeData>(initialContractTypeData)
  const [initialContractTemplate, setInitialContractTemplate] = useState<string | undefined>(undefined)
  const [contractTemplate, setContractTemplate] = useState<string | undefined>(undefined)
  const [contractTypeFormFieldsErrors, setContractTypeFormFieldsErrors] = useState<string[]>([])
  const navigate = useViewNavigate()

  const {t} = useTranslation()
  const {id} = useParams()

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const dealerId = queryParams.get('dealerId')

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data: contractTypeResponse} = useContractType(id)
  const {data: contractTypeNames} = useContractTypeNames()
  const {data: contractTemplatesResponse} = useTemplatesByCompany(userSelection.companyId)
  const {data: currentTemplate} = useTemplatesByContractType(id)

  const linkDealerToContractTypeMutation = useLinkDealerToContractType()
  const updateContractTypeMutation = useUpdateContractType()
  const createContractTypeMutation = useCreateContractType()

  const linkTemplateToContractTypeMutation = useLinkTemplateToContractType()
  const unlinkTemplateFromContractTypeMutation = useUnlinkTemplateFromContractType()

  useEffect(() => {
    if (currentTemplate && currentTemplate[0]) {
      setInitialContractTemplate(currentTemplate[0].id)
      setContractTemplate(currentTemplate[0].id)
    }
  }, [currentTemplate])

  const contractTemplatesOptions = useMemo(() => {
    if (!contractTemplatesResponse) return []
    else
      return contractTemplatesResponse
        .filter(item => item.contractType !== 'TA')
        .map(item => ({value: item.id, label: item.name}))
  }, [contractTemplatesResponse])

  const contractTypeNameOptions = useMemo(() => {
    if (!contractTypeNames) return []
    else return contractTypeNames.map(item => ({value: item, label: item}))
  }, [contractTypeNames])

  useEffect(() => {
    if (id && contractTypeResponse) {
      setContractTypeData({
        accessoryDiscount: contractTypeResponse.accessoryDiscount,
        accessoryInsuranceMinimum: contractTypeResponse.accessoryInsuranceMinimum,
        active: contractTypeResponse.active,
        advancedPayment: contractTypeResponse.advancedPayment,
        companyId: contractTypeResponse.companyId,
        disclaimer: contractTypeResponse.disclaimer,
        functionInterest: contractTypeResponse.functionInterest,
        insurancePercentage: contractTypeResponse.insurancePercentage,
        interest: contractTypeResponse.interest,
        lockFields: contractTypeResponse.lockFields,
        name: contractTypeResponse.name,
        note: contractTypeResponse.note,
        productDiscount: contractTypeResponse.productDiscount,
        productInsuranceMinimum: contractTypeResponse.productInsuranceMinimum,
        serviceDiscount: contractTypeResponse.serviceDiscount,
        type: contractTypeResponse.type,
        id: contractTypeResponse.id,
        displayExtraCosts: contractTypeResponse.displayExtraCosts,
        displayInstallationCosts: contractTypeResponse.displayInstallationCosts,
        displayServicePrice: contractTypeResponse.displayServicePrice,
        allowedDealers: contractTypeResponse.allowedDealers,
        expiry: contractTypeResponse.expiry,
        lengths: contractTypeResponse.lengths,
      })
    } else {
      setContractTypeData(prev => ({...prev, companyId: userSelection?.companyId || null}))
    }
  }, [contractTypeResponse, id])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
        {title: t(TranslationKey['Contract type search']), link: NavigationPathKey.CONTRACT_TYPE_SEARCH},
        {title: id ? t(TranslationKey['Edit contract type']) : t(TranslationKey['New contract type'])},
      ]),
    [],
  )

  /**
   * A function handling Contract Type form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form fields
   * @param percentage A flag defining if the form field holds a percentage value
   */
  const onChangeContractTypeFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
    percentage?: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = checked ?? (!percentage || Number(event.target.value) < 100 ? event.target.value : '100')
    setContractTypeFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setContractTypeData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * A function handling Contract Type dropdown form fields change
   * @param fieldName Form field name
   * @param item Dropdown item
   */
  const onChangeContractTypeDropdownFormField = (fieldName: string, item: DropdownItem | null) => {
    setIsFormDirty(true)
    setContractTypeFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setContractTypeData(prev => ({...prev, [fieldName]: item?.value ?? null}))
  }

  /**
   * A function handling Contract Type lenght form field change
   * @param event Change event
   * @param checked Checked state
   */
  const onChangeContractLenghtFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    let newValue = []
    if (checked) newValue = [...contractTypeData.lengths, Number(fieldName)].sort((a, b) => a - b)
    else newValue = contractTypeData.lengths.filter(item => item !== Number(fieldName))
    setContractTypeFormFieldsErrors(prev => prev.filter(item => item !== 'lengths'))
    setContractTypeData(prev => ({...prev, lengths: newValue}))
  }

  /**
   * A custom validation function for Contract Type form data
   * @returns If Contract Type form field containt any invalid fields
   */
  const validateData = (): boolean => {
    if (contractTypeData.lengths?.length === 0) {
      setError({
        message: t(TranslationKey['You must choose at least one contract length']),
        name: '',
      })
      return true
    }
    const notRequiredFields = ['note', 'type', 'disclaimer']

    const invalidValues = validateFields(contractTypeData).filter(item => !notRequiredFields.includes(item))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setContractTypeFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * Contract Type form fields configuration
   */
  const contractTypeFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.DROPDOWN,
      label: t(TranslationKey['Contract type name']),
      name: 'name',
      items: contractTypeNameOptions,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeDropdownFormField('name', {label: '', value: event.target.value}),
      value: contractTypeData.name,
      error: contractTypeFormFieldsErrors.includes('name'),
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      items: contractTypeOptions(),
      label: t(TranslationKey.Type),
      name: 'type',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeDropdownFormField('type', {label: '', value: event.target.value}),
      value: contractTypeData.type,
      error: contractTypeFormFieldsErrors.includes('type'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Valid for']),
      name: 'expiry',
      type: 'number',
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.expiry,
      error: contractTypeFormFieldsErrors.includes('expiry'),
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      items: contractTemplatesOptions,
      label: t(TranslationKey['Contract template']),
      name: 'contractTemplate',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setContractTemplate(event.target.value),
      value: contractTemplate,
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      name: 'active',
      label: t(TranslationKey.Active),
      checkboxes: [
        {
          checked: contractTypeData.active,
          label: t(TranslationKey.Active),
          value: contractTypeData.active,
          name: 'active',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractTypeFormField(event, checked),
        },
      ],
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      name: 'lengths',
      label: t(TranslationKey['Contract lengths']),
      itemsInColumn: 1,
      checkboxes: [
        {
          checked: contractTypeData.lengths?.includes(18),
          label: '18',
          value: contractTypeData.lengths?.includes(18),
          name: '18',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractLenghtFormField(event, checked),
        },
        {
          checked: contractTypeData.lengths?.includes(24),
          label: '24',
          value: contractTypeData.lengths?.includes(24),
          name: '24',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractLenghtFormField(event, checked),
        },
        {
          checked: contractTypeData.lengths?.includes(36),
          label: '36',
          value: contractTypeData.lengths?.includes(36),
          name: '36',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractLenghtFormField(event, checked),
        },
        {
          checked: contractTypeData.lengths?.includes(60),
          label: '60',
          value: contractTypeData.lengths?.includes(60),
          name: '60',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractLenghtFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * Contract Type form fields configuration - additional options section
   */
  const contractTypeFormFieldOptionsFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      label: t(TranslationKey['Contract product fields options']),
      itemsInColumn: 1,
      checkboxes: [
        {
          checked: contractTypeData.lockFields,
          label: t(TranslationKey['Lock fields']),
          value: contractTypeData.lockFields,
          name: 'lockFields',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractTypeFormField(event, checked),
        },
        {
          checked: contractTypeData.displayServicePrice,
          label: t(TranslationKey['Display service price']),
          value: contractTypeData.displayServicePrice,
          name: 'displayServicePrice',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractTypeFormField(event, checked),
        },
        {
          checked: contractTypeData.displayInstallationCosts,
          label: t(TranslationKey['Display installation compensation']),
          value: contractTypeData.displayInstallationCosts,
          name: 'displayInstallationCosts',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractTypeFormField(event, checked),
        },
        {
          checked: contractTypeData.displayExtraCosts,
          label: t(TranslationKey['Display extra costs']),
          value: contractTypeData.displayExtraCosts,
          name: 'displayExtraCosts',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeContractTypeFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * Contract Type form fields configuration - description section
   */
  const contractTypeFormFieldDescriptionFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Description),
      name: 'note',
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.note,
      multiline: true,
      rows: 6,
      customStyle: {height: 'unset'},
      error: contractTypeFormFieldsErrors.includes('note'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Disclaimer),
      name: 'disclaimer',
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.disclaimer,
      error: contractTypeFormFieldsErrors.includes('disclaimer'),
      multiline: true,
      rows: 6,
      customStyle: {height: 'unset'},
    },
  ]

  /**
   * Contract Type form fields configuration - discounts section
   */
  const contractTypeDiscountFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Product discount']),
      name: 'productDiscount',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.productDiscount,
      error: contractTypeFormFieldsErrors.includes('productDiscount'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Accessory discount']),
      name: 'accessoryDiscount',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.accessoryDiscount,
      error: contractTypeFormFieldsErrors.includes('accessoryDiscount'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Service discount']),
      name: 'serviceDiscount',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.serviceDiscount,
      error: contractTypeFormFieldsErrors.includes('serviceDiscount'),
      type: 'number',
    },
  ]

  /**
   * Contract Type form fields configuration - rate info section
   */
  const contractTypeRateInfoFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Advanced payment']),
      name: 'advancedPayment',
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.advancedPayment,
      error: contractTypeFormFieldsErrors.includes('advancedPayment'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Function interest']),
      name: 'functionInterest',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.functionInterest,
      error: contractTypeFormFieldsErrors.includes('functionInterest'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Interest),
      name: 'interest',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.interest,
      error: contractTypeFormFieldsErrors.includes('interest'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Insurance),
      name: 'insurancePercentage',
      percentage: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeContractTypeFormField(event, undefined, true),
      value: contractTypeData.insurancePercentage,
      error: contractTypeFormFieldsErrors.includes('insurancePercentage'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Product minimum insurance']),
      name: 'productInsuranceMinimum',
      currency: true,
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.productInsuranceMinimum,
      error: contractTypeFormFieldsErrors.includes('productInsuranceMinimum'),
      type: 'number',
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Accessory minimum insurance']),
      name: 'accessoryInsuranceMinimum',
      currency: true,
      onChange: onChangeContractTypeFormField,
      value: contractTypeData.accessoryInsuranceMinimum,
      error: contractTypeFormFieldsErrors.includes('accessoryInsuranceMinimum'),
      type: 'number',
    },
  ]

  const onSubmit = async (): Promise<void> => {
    if (validateData()) return

    let redirectId = id
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mutations: Promise<any>[] = []

    const clearAndExit = () => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.CONTRACT_TYPE_VIEW}/${redirectId}`)
    }

    if (id) {
      mutations.push(
        updateContractTypeMutation.mutateAsync({
          id,
          body: contractTypeData as ContractTypeRequest,
        }),
      )

      if (initialContractTemplate !== contractTemplate) {
        if (initialContractTemplate) {
          mutations.push(
            unlinkTemplateFromContractTypeMutation.mutateAsync({
              contractTypeId: id,
              templateId: initialContractTemplate,
            }),
          )
        }
        if (contractTemplate) {
          mutations.push(
            linkTemplateToContractTypeMutation.mutateAsync({
              contractTypeId: id,
              templateId: contractTemplate,
            }),
          )
        }
      }
    } else {
      const data = await createContractTypeMutation.mutateAsync(contractTypeData as ContractTypeRequest)
      redirectId = data.id

      if (contractTemplate) {
        mutations.push(
          linkTemplateToContractTypeMutation.mutateAsync({
            contractTypeId: data.id,
            templateId: contractTemplate,
          }),
        )
      }
      if (dealerId) {
        mutations.push(
          linkDealerToContractTypeMutation.mutateAsync({
            dealerId,
            contractTypeId: data.id,
          }),
        )
      }
    }

    await Promise.all(mutations)

    clearAndExit()
  }

  /**
   * A function handling Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  return {
    onDiscard,
    onSubmit,
    t,
    contractTypeFormFieldOptionsFormFields,
    contractTypeFormFieldDescriptionFields,
    contractTypeRateInfoFormFields,
    contractTypeDiscountFormFields,

    contractTypeData,
    contractTypeFormFields,
    id,
  }
}
